import React from "react";

type CollapseBreakpoint = "md" | "lg" | "xl" | "2xl";

interface PageHeaderProps {
  title: string | React.ReactNode;
  centerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  mobileBottomContent?: React.ReactNode;
  showRightOnMobile?: boolean;
  mobileTitleRight?: React.ReactNode;
  /**
   * Viewport width at which the header switches from the stacked mobile
   * layout (title row / tabs row / buttons row) to a single row. Default
   * "md" works for most pages, but action-heavy headers (many buttons)
   * need "lg" or "xl" since the sidebar eats into content width and the
   * viewport breakpoint alone doesn't account for that.
   */
  collapseAt?: CollapseBreakpoint;
}

// Tailwind needs statically-written class names to pick them up (no dynamic
// `${bp}:...` interpolation), so each supported breakpoint gets its own
// fully-spelled-out class set.
const COLLAPSE_CLASSES: Record<
  CollapseBreakpoint,
  {
    container: string;
    titleRow: string;
    mobileTitleSlot: string;
    titleCell: string;
    centerCell: string;
    rightCell: string;
    mobileBottom: string;
  }
> = {
  md: {
    container:
      "flex flex-col gap-4 md:grid md:grid-cols-[1fr_max-content_1fr] md:items-center md:gap-3 min-h-[40px]",
    titleRow: "flex items-center md:contents",
    mobileTitleSlot: "flex items-center gap-2 md:hidden",
    titleCell: "md:justify-self-start",
    centerCell: "flex justify-center md:justify-self-center",
    rightCell:
      "hidden md:flex items-center justify-end gap-2 md:justify-self-end w-full md:w-auto",
    mobileBottom: "flex flex-wrap items-center justify-center gap-2 w-full md:hidden",
  },
  lg: {
    container:
      "flex flex-col gap-4 lg:grid lg:grid-cols-[1fr_max-content_1fr] lg:items-center lg:gap-3 min-h-[40px]",
    titleRow: "flex items-center lg:contents",
    mobileTitleSlot: "flex items-center gap-2 lg:hidden",
    titleCell: "lg:justify-self-start",
    centerCell: "flex justify-center lg:justify-self-center",
    rightCell:
      "hidden lg:flex items-center justify-end gap-2 lg:justify-self-end w-full lg:w-auto",
    mobileBottom: "flex flex-wrap items-center justify-center gap-2 w-full lg:hidden",
  },
  xl: {
    container:
      "flex flex-col gap-4 xl:grid xl:grid-cols-[1fr_max-content_1fr] xl:items-center xl:gap-3 min-h-[40px]",
    titleRow: "flex items-center xl:contents",
    mobileTitleSlot: "flex items-center gap-2 xl:hidden",
    titleCell: "xl:justify-self-start",
    centerCell: "flex justify-center xl:justify-self-center",
    rightCell:
      "hidden xl:flex items-center justify-end gap-2 xl:justify-self-end w-full xl:w-auto",
    mobileBottom: "flex flex-wrap items-center justify-center gap-2 w-full xl:hidden",
  },
  "2xl": {
    container:
      "flex flex-col gap-4 2xl:grid 2xl:grid-cols-[1fr_max-content_1fr] 2xl:items-center 2xl:gap-3 min-h-[40px]",
    titleRow: "flex items-center 2xl:contents",
    mobileTitleSlot: "flex items-center gap-2 2xl:hidden",
    titleCell: "2xl:justify-self-start",
    centerCell: "flex justify-center 2xl:justify-self-center",
    rightCell:
      "hidden 2xl:flex items-center justify-end gap-2 2xl:justify-self-end w-full 2xl:w-auto",
    mobileBottom: "flex flex-wrap items-center justify-center gap-2 w-full 2xl:hidden",
  },
};

export const PageHeader = ({
  title,
  centerContent,
  rightContent,
  mobileBottomContent,
  showRightOnMobile,
  mobileTitleRight,
  collapseAt = "md",
}: PageHeaderProps) => {
  const mobileTitleSlot =
    mobileTitleRight ?? (showRightOnMobile ? rightContent : null);
  const cls = COLLAPSE_CLASSES[collapseAt];
  return (
    <div className={cls.container}>
      {/* Mobile row 1: title (+ optional inline right). Desktop: title left cell */}
      <div
        className={`${cls.titleRow} ${mobileTitleSlot ? "justify-between" : ""}`}
      >
        <div className={cls.titleCell}>
          {typeof title === "string" ? (
            <h1 className="text-3xl font-bold">{title}</h1>
          ) : (
            title
          )}
        </div>
        {mobileTitleSlot && (
          <div className={cls.mobileTitleSlot}>{mobileTitleSlot}</div>
        )}
      </div>
      {/* Mobile row 2 / Desktop center: center content (tabs) */}
      <div className={cls.centerCell}>{centerContent}</div>
      {/* Desktop-only right content */}
      <div className={cls.rightCell}>{rightContent}</div>
      {/* Mobile row 3: action buttons, hidden on desktop */}
      {mobileBottomContent && (
        <div className={cls.mobileBottom}>{mobileBottomContent}</div>
      )}
    </div>
  );
};

export default PageHeader;
